import React, { useState, useEffect } from 'react';
import { Modal, Form, Select, Button, Alert, Typography, Avatar } from 'antd';
import { UserOutlined, SendOutlined } from '@ant-design/icons';
import { useAssetActions } from '../../../../hooks/useAssets';
import { AssignAssetModalProps } from '../../../../types/asset.types';

const { Option } = Select;
const { Text } = Typography;

const AssignAssetModal: React.FC<AssignAssetModalProps> = ({
  visible,
  onClose,
  onSuccess,
  asset,
  employees,
  availableAssets
}) => {
  const [form] = Form.useForm();
  const { assignAsset, isLoading } = useAssetActions();

  useEffect(() => {
    if (visible) {
      form.resetFields();
    }
  }, [visible, form]);

  const handleSubmit = async (values: any) => {
    // Eğer availableAssets varsa, zimmet seçimi yapılıyor
    const assetId = availableAssets ? values.assetId : asset?.id;
    // Çalışan detay modunda userId'yi employees[0]'dan al, yoksa form'dan
    const userId = availableAssets && employees && employees.length > 0
      ? employees[0].id
      : values.userId;

    if (!assetId || !userId) return;

    try {
      await assignAsset(assetId, { userId });
      onSuccess();
      form.resetFields();
    } catch (error) {
      console.error('Assign asset failed:', error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  // Eğer availableAssets varsa zimmet seçimi yapılacak, yoksa asset gerekli
  if (!asset && !availableAssets) return null;

  const selectedEmployee = employees?.find(emp => emp.id === form.getFieldValue('userId'));

  return (
    <Modal
      title={
        <div className="d-flex align-items-center">
          <div className="bg-warning rounded-circle p-2 d-flex align-items-center justify-content-center me-3">
            <SendOutlined className="text-white" style={{ fontSize: '18px' }} />
          </div>
          <div>
            <h5 className="mb-0">Zimmet Atama</h5>
            <Text type="secondary" className="small">Zimmeti bir çalışana ata</Text>
          </div>
        </div>
      }
      open={visible}
      onCancel={handleCancel}
      width={500}
      footer={null}
      destroyOnClose
    >
      <div className="assign-asset-form">
        {/* Zimmet Bilgisi - Sadece belirli bir zimmet atanacaksa göster */}
        {asset && !availableAssets && (
          <Alert
            message={
              <div>
                <div className="d-flex align-items-center mb-2">
                  <i className="ti ti-archive me-2 text-primary"></i>
                  <strong>{asset.name}</strong>
                </div>
                <div className="d-flex justify-content-between">
                  <Text type="secondary">Seri No: {asset.serialNumber}</Text>
                  <Text type="secondary">
                    Durum: <span className="badge bg-success">Envanterde</span>
                  </Text>
                </div>
              </div>
            }
            type="info"
            showIcon={false}
            className="mb-4"
          />
        )}

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="off"
        >
          {/* Zimmet Seçimi - Sadece availableAssets varsa göster */}
          {availableAssets && (
            <Form.Item
              name="assetId"
              label="Atanacak Zimmet"
              rules={[
                { required: true, message: 'Zimmet seçimi gereklidir' }
              ]}
            >
              <Select
                placeholder="Zimmet seçin"
                showSearch
                size="large"
                optionFilterProp="label"
                filterOption={(input, option) =>
                  String(option?.searchText ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={availableAssets.map((asset) => ({
                  value: asset.id,
                  label: (
                    <div className="d-flex align-items-center">
                      <i className="ti ti-archive me-2 text-primary" />
                      <div>
                        <div className="fw-medium">{asset.name}</div>
                        <small className="text-muted">
                          Seri No: {asset.serialNumber}
                        </small>
                      </div>
                    </div>
                  ),
                  searchText: `${asset.name} ${asset.serialNumber}`,
                }))}
              />
            </Form.Item>
          )}

          {/* Çalışan Bilgisi - Seçim yerine bilgi gösterimi */}
          {availableAssets && employees && employees.length > 0 && (
            <Alert
              message={
                <div>
                  <div className="d-flex align-items-center mb-2">
                    <i className="ti ti-user me-2 text-success"></i>
                    <strong>Zimmet Atanacak Çalışan</strong>
                  </div>
                  <div className="d-flex align-items-center">
                    <Avatar
                      size="large"
                      icon={<UserOutlined />}
                      className="me-3"
                    />
                    <div>
                      <div className="fw-bold">{employees[0].firstName} {employees[0].lastName}</div>
                      <Text type="secondary">{employees[0].email}</Text>
                    </div>
                  </div>
                </div>
              }
              type="success"
              showIcon={false}
              className="mb-4"
            />
          )}

          {/* Normal mod için çalışan seçimi */}
          {!availableAssets && (
            <Form.Item
              name="userId"
              label="Atanacak Çalışan"
              rules={[
                { required: true, message: 'Çalışan seçimi gereklidir' }
              ]}
            >
              <Select
                placeholder="Çalışan seçin"
                showSearch
                size="large"
                filterOption={(input, option) =>
                  String(option?.searchText ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              >
                {employees?.map(employee => (
                  <Option
                    key={employee.id}
                    value={employee.id}
                    searchText={`${employee.firstName} ${employee.lastName} ${employee.email}`}
                  >
                    <div className="d-flex align-items-center">
                      <Avatar
                        size="small"
                        icon={<UserOutlined />}
                        className="me-2"
                      />
                      <div>
                        <div>{employee.firstName} {employee.lastName}</div>
                        <small className="text-muted">{employee.email}</small>
                      </div>
                    </div>
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          {/* Seçilen çalışan önizlemesi */}
          {selectedEmployee && (
            <div className="selected-employee-preview mb-3">
              <Alert
                message={
                  <div className="d-flex align-items-center">
                    <Avatar
                      size="small"
                      icon={<UserOutlined />}
                      className="me-2"
                    />
                    <div>
                      <strong>Seçilen Çalışan:</strong> {selectedEmployee.firstName} {selectedEmployee.lastName}
                      <br />
                      <small className="text-muted">{selectedEmployee.email}</small>
                    </div>
                  </div>
                }
                type="success"
                showIcon={false}
              />
            </div>
          )}

          <Form.Item className="mb-0">
            <div className="d-flex justify-content-end gap-2">
              <Button onClick={handleCancel} size="large">
                İptal
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={isLoading}
                size="large"
                icon={<SendOutlined />}
              >
                Zimmeti Ata
              </Button>
            </div>
          </Form.Item>
        </Form>
      </div>

      <style>{`
        .assign-asset-form .ant-form-item-label > label {
          font-weight: 600;
        }
        
        .selected-employee-preview {
          border-radius: 8px;
        }
      `}</style>
    </Modal>
  );
};

export default AssignAssetModal;
